import { useCallback, useEffect, useState } from 'react';
import { ScrollText, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import type { ApiResponse } from '@/types';

type AuditRow = {
  id: string;
  user_id?: string | null;
  action: string;
  resource: string;
  resource_id?: string | null;
  details?: Record<string, unknown> | null;
  created_at: string;
};

export function AuditLogAdminPage() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [draftSearch, setDraftSearch] = useState('');
  const [draftResource, setDraftResource] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedResource, setAppliedResource] = useState('');

  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = new URLSearchParams();
      q.set('page', String(page));
      q.set('limit', String(limit));
      q.set('sortBy', 'created_at');
      q.set('order', 'desc');
      if (appliedSearch.trim()) q.set('search', appliedSearch.trim());
      if (appliedResource.trim()) q.set('resource', appliedResource.trim());
      const res = await api.get<
        ApiResponse<AuditRow[]> & { total?: number; total_pages?: number; page?: number }
      >(`/admin/audit-log?${q.toString()}`);
      if (!res.success) throw new Error(res.error ?? 'Audit-Log konnte nicht geladen werden.');
      setRows(Array.isArray(res.data) ? res.data : []);
      setTotalPages(res.total_pages ?? 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Audit-Log konnte nicht geladen werden.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch, appliedResource]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Audit-Log</h1>
          <p className="text-sm text-muted">
            Sicherheits- und Änderungsprotokoll (nur Administratoren).
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={loading ? 'mr-2 h-4 w-4 animate-spin' : 'mr-2 h-4 w-4'} />
          Aktualisieren
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ScrollText className="h-5 w-5" />
            Filter
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="min-w-[200px] flex-1 space-y-1">
            <Label htmlFor="audit-search">Suche (Aktion/Ressource)</Label>
            <Input
              id="audit-search"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setAppliedSearch(draftSearch);
                  setAppliedResource(draftResource);
                  setPage(1);
                }
              }}
              placeholder="z. B. update, marketing_activities"
            />
          </div>
          <div className="min-w-[160px] space-y-1">
            <Label htmlFor="audit-resource">Ressource</Label>
            <Input
              id="audit-resource"
              value={draftResource}
              onChange={(e) => setDraftResource(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setAppliedSearch(draftSearch);
                  setAppliedResource(draftResource);
                  setPage(1);
                }
              }}
              placeholder="campaigns"
            />
          </div>
          <Button
            type="button"
            onClick={() => {
              setAppliedSearch(draftSearch);
              setAppliedResource(draftResource);
              setPage(1);
            }}
          >
            Anwenden
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {error ? (
            <p className="p-4 text-sm text-danger">{error}</p>
          ) : loading ? (
            <div className="p-8 text-center text-sm text-muted">Laden…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted">Keine Einträge.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead>
                  <tr className="border-b border-border bg-background text-xs text-muted dark:bg-card/50">
                    <th className="px-4 py-3 font-medium">Zeit</th>
                    <th className="px-4 py-3 font-medium">Aktion</th>
                    <th className="px-4 py-3 font-medium">Ressource</th>
                    <th className="px-4 py-3 font-medium">Ressourcen-ID</th>
                    <th className="px-4 py-3 font-medium">Nutzer</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-border">
                      <td className="whitespace-nowrap px-4 py-2 text-muted">
                        {formatDate(r.created_at)}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{r.action}</td>
                      <td className="px-4 py-2">{r.resource}</td>
                      <td className="max-w-[200px] truncate px-4 py-2 font-mono text-xs text-muted">
                        {r.resource_id ?? '—'}
                      </td>
                      <td className="max-w-[200px] truncate px-4 py-2 font-mono text-xs">{r.user_id ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && rows.length > 0 ? (
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <span className="text-xs text-muted">
                Seite {page} von {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
